import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import InvestmentCard from './InvestmentCard';

import { loadData, skipPortfolioLoading } from '@/store/investments/investmentService';
import { setBankLoadingAction, setBankDataAction, setEmptyBankDataAction, setBankErrorAction } from '@/store/bank/bank';

import { useNotification } from '@/libraries/notification/NotificationContext';
import { NOTIFICATION, PORTFOLIO_POLLING_TIME } from '@/constants';

import { getUserBankDetails } from '@/services/rest/bank-info';

import StocksCardContent from './InvestmentCards/StocksCardContent';
import MutualFundCardContent from './InvestmentCards/MutualFundCardContent';
import FutureAndOptionsCardContent from './InvestmentCards/FutureAndOptionsCardContent';
import { BankDetailsCardContent } from './InvestmentCards/BankDetailsCardContent';

import InvestmentOverview from './InvestmentOverview';
import InvestmentCardLoadingContent from './InvestmentCards/InvestmentCardLoadingSkeleton';
import { fetchIYIPortfolioThunk, setIYIPortfolioData } from '@/store/iyiPortfolio/iyiPortfolio';
import IYICardContent from './InvestmentCards/IYICardContent';
import BannerIYI from '../BannerIYI';
import VKYCPendingBanner from '@/components/sections/VKYCPendingBanner';

const InvestmentSummary = () => {
  const { sendNotification: sendToast } = useNotification(NOTIFICATION.TOAST);

  const investments = useSelector((state) => state.investments.investments);
  const invLoading = useSelector((state) => state.investments.isLoading);
  const userData = useSelector((state) => state.user.user);
  const isBankLoading = useSelector((state) => state.bank.isLoading);
  const bankAccount = useSelector((state) => state.bank.bankAccounts?.[0] || {});
  const { isLoading: isIYILoading, myInvestment } = useSelector((state) => state.iyiPortfolio);

  const [isPortfolioDataLoaded, setIsPortfolioDataLoaded] = useState(false);
  const isVKYCAlertVisible = useSelector(
    (state) => state?.featureflag?.dashboard?.vkyc?.vkycAlert?.isVisible
  );  

  const dispatch = useDispatch();

  const fetchBankDetails = async () => {
    dispatch(setBankLoadingAction(true));
    try {
      const userBankDetails = await getUserBankDetails();
      dispatch(setBankDataAction(userBankDetails));
    } catch (error) {
      dispatch(setBankErrorAction(error.message));
    }
  };

  useEffect(() => {
    const hasSSFBAccount = !!userData?.ssfbCustomerId;
    const isDematUser = !!userData?.isDematUser;

    if (hasSSFBAccount) {
      fetchBankDetails();
      if(isDematUser){
        dispatch(fetchIYIPortfolioThunk());
      } else {
        // Avoiding API failure and explicitly setting the data to empty
        dispatch(setIYIPortfolioData([]))
      }
    } else {
      dispatch(setEmptyBankDataAction());
    }
  }, [userData]);

  const isBankAccountEmpty = Object.entries(bankAccount).length === 0;
  const isSipOrderEmpty = myInvestment?.length === 0;

  const showInvestmentOverview = !!(
    bankAccount?.availableBalance ||
    investments?.mutualFunds?.investment ||
    investments?.stocks?.investment ||
    investments?.fno?.margin
  );

  const loadPortfolio = () => {
    return loadData().catch(() => {
      sendToast({
        title: 'Failed to load Portfolio',
        clearExisting: true,
      });
    });
  };

  useEffect(() => {
    if(userData.isDematUser){
      const intervalId = setInterval(() => {
        loadData().then(() => {
          if (!isPortfolioDataLoaded) setIsPortfolioDataLoaded(true);
        });
      }, PORTFOLIO_POLLING_TIME);

      return () => {
        clearInterval(intervalId);
      };
    }
  }, [isPortfolioDataLoaded]);

  useEffect(() => {
    if(userData.isDematUser){
      loadPortfolio().then(() => {
        setIsPortfolioDataLoaded(true);
      });
    } else {
      skipPortfolioLoading()
      setIsPortfolioDataLoaded(true);
    }
  }, []);

  const isIYIEnabled =
    import.meta.env.VITE_PRODUCT_ENABLED_BANK === 'true' &&
    import.meta.env.VITE_PRODUCT_ENABLED_IYI === 'true' &&
    !isIYILoading &&
    !isSipOrderEmpty;

  const productList = [
    // TODO: Move product specific logic inside cards
    {
      id: 'bank',
      isEnabled: import.meta.env.VITE_PRODUCT_ENABLED_BANK === 'true',
      isDematUser: true,
      isLoading: isBankLoading,
      title: 'SAVINGS ACCOUNT',
      // title: bankAccount?.schemeDescription == "INSTA SAVINGS ACCOUNT" ? "SAVINGS ACCOUNT" : bankAccount?.schemeDescription || "Earn",
      path: '/digital-bank',
      headingIcon: bankAccount ? 'savings' : null,
      variant:
        bankAccount?.balance != 0 ? 'orange-bordered' : 'orange-filled',
      customTitle: !userData?.ssfbCustomerId,
      data: bankAccount,
      content: <BankDetailsCardContent data={bankAccount} />,
    },
    {
      id: 'stocks',
      isEnabled: true,
      isDematUser: userData?.isDematUser,
      title: 'Stocks',
      path: '/stocks/positions',
      headingIcon: 'stock1',
      variant: investments?.stocks?.investment == 0 ? 'green-filled' : 'green-bordered',
      data: investments.stocks,
      hasInvestment: investments?.stocks?.investment != 0,
      content: <StocksCardContent data={investments.stocks} isDematUser={userData?.isDematUser} />,
    },
    {
      id: 'mutualFunds',
      isEnabled: import.meta.env.VITE_PRODUCT_ENABLED_MF === 'true',
      isDematUser: userData?.isDematUser,
      title: 'Mutual Funds',
      path: '/mutual-funds/portfolio',
      headingIcon: 'mutual-fund',
      variant: investments?.mutualFunds?.investment == 0 ? 'black-filled' : 'black-bordered',
      hasInvestment: investments?.mutualFunds?.investment != 0,
      data: investments.mutualFunds,
      content: <MutualFundCardContent data={investments.mutualFunds} isDematUser={userData?.isDematUser} />,
    },
    {
      id: 'futuresOptions',
      isEnabled: true,
      isDematUser: userData?.isDematUser,
      title: 'Future & Options',
      path: '/stocks/positions',
      headingIcon: 'f&o',
      variant: investments?.fno?.margin == 0 ? 'yellow-filled' : 'yellow-bordered',
      hasInvestment: investments?.fno?.margin != 0,
      data: investments.fno,
      content: <FutureAndOptionsCardContent data={investments.fno} isDematUser={userData?.isDematUser} />,
    },
    {
      id: 'iyi',
      isEnabled: isIYIEnabled,
      isDematUser: false,
      isLoading: isIYILoading,
      title: 'IYI',
      path: '/digital-bank/investment',
      headingIcon: 'iyi',
      variant: 'purple-bordered',
      hasInvestment: true,
      data: myInvestment,
      content: <IYICardContent data={myInvestment} />,
    },
  ];

  const activeProducts = productList.filter((product) => product.isEnabled);

  return (
    <>
      {isVKYCAlertVisible && <VKYCPendingBanner />}
      <div className="tw-max-w-screen-lg tw-w-full tw-bg-white tw-p-4 tw-rounded-[16px] tw-flex-1 tw-flex tw-flex-col tw-gap-4">
        <div className="tw-grid tw-gap-8 tw-grid-cols-1 xl:tw-grid-cols-2">
          <div className="tw-grid tw-gap-4 tw-grid-cols-1 lg:tw-grid-cols-2 xl:tw-col-span-2">
            {showInvestmentOverview && isPortfolioDataLoaded && (
              <InvestmentOverview
                activeProducts={activeProducts}
                data={investments}
                bankAccountDetails={bankAccount}
              />
            )}
            {activeProducts.map((product) =>
              !isPortfolioDataLoaded || product.isLoading ? (
                <InvestmentCard key={product.id} variant="gray-bordered">
                  <InvestmentCardLoadingContent />
                </InvestmentCard>
              ) : (
                <InvestmentCard
                  key={product.id}
                  data={product}
                  onReload={loadPortfolio}
                  variant={product.variant}
                  isLoading={invLoading}
                >
                  {product.content}
                </InvestmentCard>
              )
            )}
          </div>
        </div>
        {!isBankAccountEmpty && isSipOrderEmpty && !isIYILoading && <BannerIYI />}
      </div>
    </>
  );
};

export default InvestmentSummary;
